import { useEffect } from 'react';

/**
 * Императивно управляет содержимым <head>: title, meta description, canonical,
 * Open Graph, Twitter Card и JSON-LD. Используется вместо react-helmet-async.
 *
 * Работает при прямой загрузке страницы и подхватывается пререндером (react-snap),
 * который сериализует готовый DOM. При размонтировании компонента возвращает
 * прежний title и удаляет созданные теги.
 *
 * @param {object}  opts
 * @param {string}  [opts.title]        — document.title (+ og:title, twitter:title)
 * @param {string}  [opts.description]  — meta description (+ og/twitter description)
 * @param {string}  [opts.canonical]    — link rel="canonical" (+ og:url)
 * @param {string}  [opts.image]        — og:image / twitter:image (абсолютный URL)
 * @param {object}  [opts.og]           — доп. Open Graph теги { 'og:type': 'website', ... }
 * @param {object}  [opts.twitter]      — доп. Twitter теги { 'twitter:card': '...', ... }
 * @param {object}  [opts.jsonLd]       — structured data (будет JSON.stringify)
 */
export function useSeoHead({
    title,
    description,
    canonical,
    image,
    og = {},
    twitter = {},
    jsonLd,
} = {}) {
    // Сериализуем объекты, чтобы хук не перезапускался от новой ссылки на каждый рендер.
    const ogKey = JSON.stringify(og);
    const twitterKey = JSON.stringify(twitter);
    const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : '';

    useEffect(() => {
        const created = [];

        const upsert = (selector, tag, attrs) => {
            let el = document.head.querySelector(selector);
            if (!el) {
                el = document.createElement(tag);
                el.setAttribute('data-seo', '');
                document.head.appendChild(el);
                created.push(el); // удаляем при очистке только то, что создали сами
            }
            Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
        };

        const prevTitle = document.title;
        if (title) document.title = title;

        if (description) {
            upsert('meta[name="description"]', 'meta', { name: 'description', content: description });
        }
        if (canonical) {
            upsert('link[rel="canonical"]', 'link', { rel: 'canonical', href: canonical });
        }

        const ogTags = {
            ...(title && { 'og:title': title }),
            ...(description && { 'og:description': description }),
            ...(canonical && { 'og:url': canonical }),
            ...(image && { 'og:image': image }),
            ...JSON.parse(ogKey),
        };
        Object.entries(ogTags).forEach(([property, content]) =>
            upsert(`meta[property="${property}"]`, 'meta', { property, content }));

        const twitterTags = {
            ...(title && { 'twitter:title': title }),
            ...(description && { 'twitter:description': description }),
            ...(image && { 'twitter:image': image }),
            ...JSON.parse(twitterKey),
        };
        Object.entries(twitterTags).forEach(([name, content]) =>
            upsert(`meta[name="${name}"]`, 'meta', { name, content }));

        if (jsonLdKey) {
            const ld = document.createElement('script');
            ld.type = 'application/ld+json';
            ld.setAttribute('data-seo', '');
            ld.textContent = jsonLdKey;
            document.head.appendChild(ld);
            created.push(ld);
        }

        return () => {
            document.title = prevTitle;
            created.forEach((el) => el.remove());
        };
    }, [title, description, canonical, image, ogKey, twitterKey, jsonLdKey]);
}

export default useSeoHead;
